/**
 * A crash inside one module or widget stays there: its box shows what broke, the rest of the kiosk
 * keeps working. The error also goes to the diagnostics list.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { report } from '../core/diagnostics';
import { useTranslate } from '../react/hooks';
import { Problem } from '../ui-kit/Panel';

interface Props {
  /** Who is inside, for the message: "module chat", "widget seat-map". */
  name: string;
  /** Changing it clears the error (a new screen gets a new chance). */
  resetKey?: unknown;
  children: ReactNode;
}

interface State {
  error: Error | null;
  key: unknown;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, key: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey !== state.key ? { error: null, key: props.resetKey } : null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    report('error', this.props.name, error.message, { stack: error.stack, componentStack: info.componentStack });
  }

  override render() {
    if (this.state.error) {
      return <Crash name={this.props.name} error={this.state.error} />;
    }
    return this.props.children;
  }
}

/** What a crashed part shows: a short sentence in the kiosk's language; who broke, and how, folded. */
function Crash({ name, error }: { name: string; error: Error }) {
  const t = useTranslate();
  const detail = [`${name}: ${error.message}`, ...(error.stack?.split('\n').slice(1, 5) ?? [])].join('\n');
  return <Problem title={t('error.part_failed')} detail={detail} detailLabel={t('error.details')} />;
}
